/**
 * What the server checks about an uploaded drawing — by its bytes, never by
 * its name or by what the client says (spec 7: PNG, three base views). Without
 * an image library: the PNG signature, a well-formed IHDR chunk as the first
 * chunk (length 13, valid CRC, a bit depth the colour type allows) and an IEND
 * chunk at the end. Width and height are read from IHDR; the client never
 * states them. They are what every pin on the drawing is normalised against
 * (core/geometry.ts), so a wrong number here moves pins.
 *
 * The whole file is read (≤ 5 MB): the API streams drawings to members anyway,
 * and a file whose IEND is missing was cut off on the way up.
 */

export const MAX_DRAWING_BYTES = 5 * 1024 * 1024;
/**
 * The LONGER edge must lie in 400…4000 px, the shorter one must be at least
 * 100 px. Not "both edges 400…4000": a front view is a wide, flat strip — the
 * catalogue's are 1600 × 369 (DA40), 1600 × 446 (PA28), 1600 × 502 (C172) —
 * and a rule both edges had to meet would refuse exactly the drawings the
 * platform ships. 4000 keeps a drawing within what a phone decodes without
 * trouble; 400 keeps a pin's 44-px tap target from covering a tenth of it.
 */
export const MIN_DRAWING_EDGE = 400;
export const MAX_DRAWING_EDGE = 4000;
export const MIN_DRAWING_SHORT_EDGE = 100;

const SIGNATURE = [0x89, 0x50, 0x4e, 0x47, 0x0d, 0x0a, 0x1a, 0x0a];
/** Signature + IHDR (4 length + 4 type + 13 data + 4 CRC). */
const HEADER_BYTES = 8 + 25;
const IEND = [0, 0, 0, 0, 0x49, 0x45, 0x4e, 0x44, 0xae, 0x42, 0x60, 0x82];

/** Bit depths each PNG colour type allows (PNG spec, table 11.1). */
const DEPTHS: Readonly<Record<number, readonly number[]>> = {
  0: [1, 2, 4, 8, 16],
  2: [8, 16],
  3: [1, 2, 4, 8],
  4: [8, 16],
  6: [8, 16],
};

let crcTable: Uint32Array | null = null;

/** CRC-32 as PNG uses it (ISO 3309, polynomial 0xEDB88320). */
export function crc32(bytes: Uint8Array): number {
  if (!crcTable) {
    crcTable = new Uint32Array(256);
    for (let n = 0; n < 256; n++) {
      let c = n;
      for (let k = 0; k < 8; k++) c = c & 1 ? 0xedb88320 ^ (c >>> 1) : c >>> 1;
      crcTable[n] = c >>> 0;
    }
  }
  let crc = 0xffffffff;
  for (const b of bytes) crc = crcTable[(crc ^ b) & 0xff]! ^ (crc >>> 8);
  return (crc ^ 0xffffffff) >>> 0;
}

function u32(bytes: Uint8Array, at: number): number {
  return (
    ((bytes[at]! << 24) | (bytes[at + 1]! << 16) | (bytes[at + 2]! << 8) | bytes[at + 3]!) >>> 0
  );
}

export type PngVerdict =
  | {
      readonly ok: true;
      readonly width: number;
      readonly height: number;
      /** Colour type 4 or 6, or a tRNS chunk somewhere: the background can be transparent. */
      readonly alpha: boolean;
    }
  | { readonly ok: false; readonly reason: 'not_png' | 'truncated' };

/**
 * Reads the header of `bytes`. `not_png` for a wrong signature, an IHDR that
 * is not the first chunk, has the wrong length, a CRC that does not match, a
 * zero size or a colour type / bit depth combination PNG does not have;
 * `truncated` when the file is shorter than its header or does not end with
 * IEND. Size limits are a separate step (`validateDrawing`).
 */
export function inspectPng(bytes: Uint8Array): PngVerdict {
  if (bytes.length < SIGNATURE.length || SIGNATURE.some((b, i) => bytes[i] !== b)) {
    return { ok: false, reason: 'not_png' };
  }
  if (bytes.length < HEADER_BYTES) return { ok: false, reason: 'truncated' };
  const length = u32(bytes, 8);
  const type = String.fromCharCode(...bytes.subarray(12, 16));
  if (length !== 13 || type !== 'IHDR') return { ok: false, reason: 'not_png' };
  const crc = u32(bytes, 29);
  if (crc32(bytes.subarray(12, 29)) !== crc) return { ok: false, reason: 'not_png' };
  const width = u32(bytes, 16);
  const height = u32(bytes, 20);
  const depth = bytes[24]!;
  const colour = bytes[25]!;
  const [compression, filter, interlace] = [bytes[26]!, bytes[27]!, bytes[28]!];
  if (width === 0 || height === 0 || width > 0x7fffffff || height > 0x7fffffff) {
    return { ok: false, reason: 'not_png' };
  }
  if (!DEPTHS[colour]?.includes(depth)) return { ok: false, reason: 'not_png' };
  if (compression !== 0 || filter !== 0 || interlace > 1) return { ok: false, reason: 'not_png' };
  const tail = bytes.subarray(bytes.length - IEND.length);
  if (bytes.length < HEADER_BYTES + IEND.length || IEND.some((b, i) => tail[i] !== b)) {
    return { ok: false, reason: 'truncated' };
  }
  return {
    ok: true,
    width,
    height,
    alpha: colour === 4 || colour === 6 || hasChunk(bytes, 'tRNS'),
  };
}

/** Walks the chunk list after IHDR looking for `name`; stops at anything malformed. */
function hasChunk(bytes: Uint8Array, name: string): boolean {
  let at = HEADER_BYTES;
  while (at + 12 <= bytes.length) {
    const length = u32(bytes, at);
    const type = String.fromCharCode(...bytes.subarray(at + 4, at + 8));
    if (type === name) return true;
    if (type === 'IDAT' || type === 'IEND') return false;
    at += 12 + length;
  }
  return false;
}

export interface UploadedDrawing {
  readonly size: number;
  readonly contentType: string | null;
  readonly bytes: Uint8Array;
}

export type DrawingProblem =
  'missing' | 'too_large' | 'content_type' | 'not_png' | 'truncated' | 'dimensions';

export type DrawingCheck =
  | { readonly ok: true; readonly width: number; readonly height: number; readonly alpha: boolean }
  | { readonly ok: false; readonly problem: DrawingProblem };

/** Everything the registration checks about the uploaded blob, in one place. */
export function validateDrawing(blob: UploadedDrawing | null): DrawingCheck {
  if (!blob) return { ok: false, problem: 'missing' };
  if (blob.size > MAX_DRAWING_BYTES) return { ok: false, problem: 'too_large' };
  if (blob.contentType !== 'image/png') return { ok: false, problem: 'content_type' };
  const verdict = inspectPng(blob.bytes);
  if (!verdict.ok) return { ok: false, problem: verdict.reason };
  const long = Math.max(verdict.width, verdict.height);
  const short = Math.min(verdict.width, verdict.height);
  if (long < MIN_DRAWING_EDGE || long > MAX_DRAWING_EDGE || short < MIN_DRAWING_SHORT_EDGE) {
    return { ok: false, problem: 'dimensions' };
  }
  return { ok: true, width: verdict.width, height: verdict.height, alpha: verdict.alpha };
}

/** What the person reads for each problem. */
export const DRAWING_MESSAGES: Readonly<Record<DrawingProblem, string>> = {
  missing: 'Die Zeichnung ist nicht angekommen. Bitte erneut hochladen.',
  too_large: 'Die Zeichnung ist größer als 5 MB.',
  content_type: 'Zeichnungen müssen als image/png hochgeladen werden.',
  not_png: 'Das ist keine PNG-Datei.',
  truncated: 'Die PNG-Datei ist unvollständig.',
  dimensions: `Die längere Seite der Zeichnung muss ${MIN_DRAWING_EDGE} bis ${MAX_DRAWING_EDGE} Pixel lang sein, die kürzere mindestens ${MIN_DRAWING_SHORT_EDGE} Pixel.`,
};
